import type React from "react";
import {createContext, useCallback, useEffect, useMemo, useState} from "react";

import {type Props as ConfirmModalProps} from "../../components/ModalConfirm";

interface AppContextType {
    isMobile: boolean;
    confirmModal: ConfirmModalProps<any> | undefined;
    isTouchDevice: boolean;
    setConfirmModal: (
        modal:
            | ConfirmModalProps<any>
            | ((prev: ConfirmModalProps<any>) => ConfirmModalProps<any>)
            | undefined
    ) => void;
    setIsMobile: (value: boolean) => void;
}

const init: AppContextType = {
    confirmModal: undefined,
    isMobile: false,
    isTouchDevice: false,
    setConfirmModal: () => {},
    setIsMobile: () => {}
};

export const AppContext = createContext<AppContextType>(init);

export const AppProvider: React.FC<{
    children: React.ReactNode;
}> = ({children}) => {
    const [context, setContext] = useState<AppContextType>({
        ...init
    });

    const [isMobile, setIsMobile] = useState(() => {
        if (!process.env.IS_WEB) {
            return false;
        }
        const mediaQuery = window.matchMedia("(max-width: 768px)");
        return mediaQuery.matches;
    });

    useEffect(() => {
        if (!process.env.IS_WEB) {
            return;
        }
        const mediaQuery = window.matchMedia("(max-width: 768px)"); // Match the `mobile` breakpoint
        const handleResize = () => setIsMobile(mediaQuery.matches);

        handleResize();

        mediaQuery.addEventListener("change", handleResize);

        return () => mediaQuery.removeEventListener("change", handleResize);
    }, [setIsMobile]);

    const setConfirmModal = useCallback<AppContextType["setConfirmModal"]>(modal => {
        if (typeof modal === "function") {
            setContext(prev => {
                return {
                    ...prev,
                    confirmModal: prev.confirmModal ? modal(prev.confirmModal) : undefined
                };
            });
            return;
        }
        setContext(prev => ({
            ...prev,
            confirmModal: modal
                ? {
                      ...prev.confirmModal,
                      ...modal
                  }
                : undefined
        }));
    }, []);

    const value = useMemo(
        () => ({
            ...context,
            isMobile,
            setConfirmModal
        }),
        [context, isMobile, setConfirmModal]
    );

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
